/**
 * Two-spine placement — only 左 2.1 bottom and 右 2.2→2.1 bottom (same tips as 全球玩家表).
 * No drill-down to arbitrary nodes.
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { binaryTreeAPI, type GlobalTableLegTip } from '../api/binary-tree';
import { THEMED_SURFACE_COLORS as C, ThemedPanel } from './ui/ThemedSurface';
import type { PlacementSelection } from './PlacementTreePicker';

function formatLegPath(tip: GlobalTableLegTip | null | undefined): string {
  if (!tip?.path_serials?.length) return '—';
  return tip.path_serials.join('/');
}

function SpineCard({
  label,
  tipLabel,
  tip,
  selected,
  registerLabel,
  expandLabel,
  disabled,
  onRegister,
  onJump,
}: {
  label: string;
  tipLabel: string;
  tip: GlobalTableLegTip | null | undefined;
  selected: boolean;
  registerLabel: string;
  expandLabel: string;
  disabled?: boolean;
  onRegister: () => void;
  onJump?: () => void;
}) {
  const playerId = tip ? (tip.player_number ?? String(tip.user_id)) : '—';
  return (
    <ThemedPanel
      className="flex-1 p-3"
      style={{
        borderColor: selected ? `${C.gold}88` : undefined,
        background: selected ? `${C.gold}14` : undefined,
      }}
    >
      <div className="text-xs font-semibold" style={{ color: C.gold }}>
        {label}
      </div>
      <div className="mt-1 text-[11px]" style={{ color: C.text }}>
        ID: {playerId}
      </div>
      <div className="mt-0.5 text-[10px]" style={{ color: C.muted }}>
        {tipLabel}({formatLegPath(tip)})
      </div>
      {tip && onJump ? (
        <button
          type="button"
          onClick={onJump}
          className="mt-2 text-[10px] font-semibold"
          style={{ color: '#e85d5d' }}
        >
          {expandLabel} ↓
        </button>
      ) : null}
      <button
        type="button"
        disabled={disabled}
        onClick={onRegister}
        className="mt-3 w-full rounded-lg border px-2 py-2 text-[11px] font-semibold disabled:cursor-not-allowed disabled:opacity-40"
        style={{ borderColor: `${C.gold}88`, color: C.gold, background: `${C.gold}14` }}
      >
        {registerLabel}
      </button>
    </ThemedPanel>
  );
}

export function TwoSpinePlacementPicker({
  rootUserId,
  rootUsername,
  selection,
  onSelect,
}: {
  rootUserId: number;
  rootUsername: string;
  selection: PlacementSelection | null;
  onSelect: (sel: PlacementSelection | null) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const tableQuery = useQuery({
    queryKey: ['binary-tree', 'two-spine-tips', rootUserId],
    queryFn: () =>
      binaryTreeAPI.getGlobalTable({
        root_user_id: rootUserId,
        page: 1,
        limit: 80,
        max_depth: 3,
      }),
    staleTime: 15_000,
  });

  const footer = tableQuery.data?.footer;
  const leftTip = footer?.left_tip ?? null;
  const rightTip = footer?.right_tip ?? null;
  const hasLeftLine = !!leftTip;
  const rootPlayer = footer?.root_player_number ?? String(rootUserId);
  const rootAsTip: GlobalTableLegTip = {
    user_id: rootUserId,
    player_number: rootPlayer,
    path_serials: [],
    has_deeper: false,
  };
  const leftCardTip = leftTip ?? rootAsTip;
  const rightCardTip = rightTip ?? (hasLeftLine ? rootAsTip : null);

  function jumpToTip(tip: GlobalTableLegTip) {
    const num = tip.player_number?.trim() || String(tip.user_id);
    navigate(`/global-players?root=${encodeURIComponent(num)}&root_id=${tip.user_id}`);
  }

  return (
    <div className="space-y-3">
      <p className="text-xs leading-relaxed" style={{ color: C.muted }}>
        {t(
          'registerMember.treeHintDrill',
          '登录后只能挂两条主线最底部：左区 2.1→2.1…；右区先 2.2 再沿该线 2.1→2.1…。2.1 空时必须先落 2.1。',
        )}
      </p>

      {tableQuery.isError ? (
        <div className="rounded-xl border px-3 py-6 text-center text-xs" style={{ borderColor: C.line, color: '#f87171' }}>
          {t('common.loadFailed', '加载失败，请重试')}
        </div>
      ) : null}

      {tableQuery.isLoading && !footer ? (
        <div className="rounded-xl border px-3 py-6 text-center text-xs" style={{ borderColor: C.line, color: C.muted }}>
          {t('common.loading', '加载中…')}
        </div>
      ) : (
        <div className="flex gap-2">
          <SpineCard
            label={t('globalPlayers.legLeft', '左')}
            tipLabel={t('globalPlayers.legBottomLeft', '最左下')}
            tip={leftCardTip}
            selected={selection?.position === 'L'}
            registerLabel={t('registerMember.placeLeftSpill', '左区注册（滑落）')}
            expandLabel={t('globalPlayers.expandDown', '一键到底')}
            onRegister={() =>
              onSelect({ userId: rootUserId, username: rootUsername, position: 'L' })
            }
            onJump={() => jumpToTip(leftCardTip)}
          />
          <SpineCard
            label={t('globalPlayers.legRight', '右')}
            tipLabel={t('globalPlayers.legBottomRight', '最左下')}
            tip={rightCardTip}
            selected={selection?.position === 'R'}
            registerLabel={
              hasLeftLine
                ? t('registerMember.placeRightSpill', '右区注册（滑落）')
                : t('registerMember.placeRightNeedLeft', '请先落左区 2.1')
            }
            expandLabel={t('globalPlayers.expandDown', '一键到底')}
            disabled={!hasLeftLine}
            onRegister={() => {
              if (!hasLeftLine) return;
              onSelect({ userId: rootUserId, username: rootUsername, position: 'R' });
            }}
            onJump={rightCardTip ? () => jumpToTip(rightCardTip) : undefined}
          />
        </div>
      )}

      {selection ? (
        <div className="rounded-xl border px-3 py-2 text-xs" style={{ borderColor: `${C.gold}66`, color: C.text }}>
          {t('registerMember.selectedSpine', {
            zone: selection.position === 'L' ? t('auth.positionLeft', '左区') : t('auth.positionRight', '右区'),
            tip:
              selection.position === 'L'
                ? (leftTip?.player_number ?? rootPlayer)
                : (rightTip?.player_number ?? rootPlayer),
            defaultValue: `已选{{zone}}：将挂在该线最底部（{{tip}}）之下`,
          })}
        </div>
      ) : (
        <div className="rounded-xl border px-3 py-2 text-xs" style={{ borderColor: C.line, color: C.muted }}>
          {t('registerMember.pickPlacement', '请先选择左区或右区滑落')}
        </div>
      )}
    </div>
  );
}
